import { IconButton, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Configure, Index } from 'react-instantsearch-dom';

import { useKeyCommand } from '@/hooks/useKeyCommand';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { InstantSearchWithErrorLogging } from '@/imports/ui/components/search/InstantSearchWithErrorLogging';
import { useGlobalSearchContext } from '@/imports/ui/components/search/SearchProvider';
import { useIsMobile } from '@/imports/ui/responsive/Responsive';
import {
  useWebflowUtilitiesLifecycle,
  WEBFLOW_SEARCH_HOLD_KEY,
} from '@/lib/webflow-poc/webflow-utilities-lifecycle';

import { GlobalSearchModal } from './components/GlobalSearchModal';

export const GlobalSearch: React.FC = (props) => {
  const searchClient = useGlobalSearchContext();
  const memberManagement = useMemberManagement();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useIsMobile();
  // Present only under Webflow utilities portals — keeps search mounted if the
  // mobile Webflow menu closes the utilities slot on icon click.
  const webflowUtilitiesLifecycle = useWebflowUtilitiesLifecycle();
  const webflowMobileHybrid =
    Boolean(webflowUtilitiesLifecycle) && isMobile;

  const router = useRouter();

  // Hold helpers change identity when isSearchHeld flips — keep a ref so
  // open/close callbacks stay stable (otherwise the asPath effect below
  // re-runs and immediately dismisses the modal).
  const webflowLifecycleRef = React.useRef(webflowUtilitiesLifecycle);
  webflowLifecycleRef.current = webflowUtilitiesLifecycle;

  const openSearch = React.useCallback(() => {
    // Hold on click (not mousedown) so we never hide/disable the icon strip
    // before this click finishes — that was preventing desktop open entirely.
    webflowLifecycleRef.current?.holdOpen(WEBFLOW_SEARCH_HOLD_KEY);
    onOpen();
  }, [onOpen]);

  const closeSearch = React.useCallback(() => {
    onClose();
    webflowLifecycleRef.current?.releaseHold(WEBFLOW_SEARCH_HOLD_KEY);
  }, [onClose]);

  useKeyCommand('metaKey', 'k', openSearch);

  // https://nextjs.org/docs/api-reference/next/router#resetting-state-after-navigation
  React.useEffect(() => {
    closeSearch();
  }, [router.asPath, closeSearch]);

  if (
    !searchClient.client ||
    !searchClient.indexData ||
    memberManagement.loading ||
    !router.isReady
  ) {
    return (
      <Tooltip
        label="Your device is unable to connect to search service"
        isDisabled={memberManagement.loading}
      >
        <IconButton
          aria-label="Search"
          icon={<SearchIcon />}
          variant="outline"
          colorScheme="gray"
          isRound
          isDisabled
        />
      </Tooltip>
    );
  }

  return (
    <InstantSearchWithErrorLogging
      searchClient={searchClient.client}
      indexName={searchClient.indexData.postIndexName}
    >
      <Index indexName={searchClient.indexData.memberIndexName}>
        <Configure
          hitsPerPage={5}
          restrictSearchableAttributes={[
            'displayName',
            searchClient.indexData.memberIndexSearchableField,
          ]}
          filters={searchClient.indexFilterData?.memberIndexFilter}
          attributesToRetrieve={[
            'displayName',
            searchClient.indexData.memberIndexViewableField,
            'userId',
            'profilePictureUrl',
          ]}
        />
      </Index>
      <Index indexName={searchClient.indexData.orgIndexName}>
        <Configure
          hitsPerPage={5}
          filters={searchClient.indexFilterData?.orgIndexFilter}
        />
      </Index>
      <Index indexName={searchClient.indexData.eventIndexName}>
        <Configure hitsPerPage={5} />
      </Index>
      <Index indexName={searchClient.indexData.fileIndexName}>
        <Configure
          hitsPerPage={5}
          attributesToRetrieve={[
            'objectID',
            'file.name',
            'file.type',
            'file.size',
            'file.dateCreated',
            'libraryFolderId',
            'libraryId',
            'scope',
            'displayName',
            'author',
            'bodyPlainText',
            searchClient.indexData.memberIndexViewableField,
          ]}
        />
      </Index>
      <Index indexName={searchClient.indexData.postIndexName}>
        <Configure
          hitsPerPage={5}
          attributesToRetrieve={[
            'displayName',
            'authorId',
            'profilePictureUrl',
            'postType',
            'title',
            'bodyPlainText',
            'datePublished',
            'numComments',
            'reactionCount',
            'organization',
            'group',
            'blog',
            'poll',
            'question',
            'answer',
            'post',
            searchClient.indexData.memberIndexViewableField,
          ]}
        />
      </Index>

      <IconButton
        aria-label="Search"
        icon={<SearchIcon />}
        variant="outline"
        colorScheme="gray"
        isRound
        onClick={(event) => {
          // Stop Webflow treating the utilities portal click as "outside" the menu.
          if (webflowUtilitiesLifecycle) {
            event.stopPropagation();
          }
          openSearch();
        }}
      />

      <GlobalSearchModal
        isOpen={isOpen}
        onClose={closeSearch}
        elevateAboveWebflowUtilities={Boolean(webflowUtilitiesLifecycle)}
        webflowMobileHybrid={webflowMobileHybrid}
      />
    </InstantSearchWithErrorLogging>
  );
};
